import { createClient } from '@supabase/supabase-js';
import * as tus from 'tus-js-client';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env.local file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper function to get edge function URL
export const getEdgeFunctionUrl = (functionName) => {
  const baseUrl = import.meta.env.VITE_EDGE_FUNCTION_URL || 
    `${supabaseUrl}/functions/v1`;
  return `${baseUrl}/${functionName}`;
};

// Helper function to upload file and process data
export const uploadAndProcessFile = async (file, sessionId = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    if (sessionId) {
      formData.append('session_id', sessionId);
    }

    // Updated to match your actual edge function name!
    const functionName = 'ames-session-etl';
    
    const response = await fetch(getEdgeFunctionUrl(functionName), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

// Get current user's companies and memberships
export const getUserCompanies = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const baseSelect = `
    id,
    role,
    companies (
      id,
      name,
      invite_code,
      created_at
    )
  `;

  const withOrgTypeSelect = `
    id,
    role,
    companies (
      id,
      name,
      org_type,
      invite_code,
      created_at
    )
  `;

  const normalizeCompanies = (rows = []) => rows.map((row) => ({
    ...row,
    companies: row.companies
      ? { org_type: 'contractor', ...row.companies }
      : row.companies,
  }));

  const queryMemberships = (selectClause) => supabase
    .from('memberships')
    .select(selectClause)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  let { data, error } = await queryMemberships(withOrgTypeSelect);

  if (error && error.message?.includes('org_type')) {
    console.warn('companies.org_type is not available yet; defaulting organization type to contractor.');
    ({ data, error } = await queryMemberships(baseSelect));
  }

  if (error) {
    console.error('Error fetching user companies:', error);
    return [];
  }

  return normalizeCompanies(data || []);
};

// Get user's current active company (first one for now)
export const getCurrentCompany = async () => {
  const companies = await getUserCompanies();
  return companies.length > 0 ? companies[0].companies : null;
};

const isMissingProjectAccessRoleRpc = (error) => {
  const message = error?.message || '';
  return error?.code === 'PGRST202' ||
    message.includes('get_project_access_role') ||
    message.includes('Could not find the function');
};

const getProjectAccessRoleCompat = async (projectId, ownerCompanyId) => {
  const { data: accessRole, error } = await supabase.rpc('get_project_access_role', {
    p_project_id: projectId,
  });

  if (!error) return accessRole;
  if (!isMissingProjectAccessRoleRpc(error)) throw error;
  if (!ownerCompanyId) return null;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: membership, error: membershipError } = await supabase
    .from('memberships')
    .select('company_id')
    .eq('user_id', user.id)
    .eq('company_id', ownerCompanyId)
    .maybeSingle();

  if (membershipError) throw membershipError;
  return membership ? 'owner' : null;
};

// Company creation and joining are handled by edge functions:
// - create-company: Creates company + internal entitlement trial + membership
// - add-to-company: Joins an existing company by invite code

// Check if user needs onboarding (has no companies)
// Replace your needsOnboarding function in supabaseClient.js with this:

export const needsOnboarding = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return true;
    }

    const companies = await getUserCompanies();
    return !companies || companies.length === 0;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return true;
  }
};

// Get or create user profile
export const getUserProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error && error.code === 'PGRST116') {
    // Profile doesn't exist, create it
    const { data: newProfile, error: createError } = await supabase
      .from('profiles')
      .insert({
        user_id: user.id,
        email: user.email,
        onboarding_completed: false
      })
      .select()
      .single();

    if (createError) {
      console.error('Error creating profile:', createError);
      return null;
    }
    return newProfile;
  }

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return profile;
};

// Update user profile
export const updateUserProfile = async (updates) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .upsert({
      user_id: user.id,
      email: user.email,
      ...updates,
    }, {
      onConflict: 'user_id',
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getCompanyProjects = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get user's current company
  const { data: membership } = await supabase
    .from('memberships')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (!membership) throw new Error('No company membership found');

  // Get projects with session counts - FIXED: Use correct table name
  const { data: projects, error } = await supabase
    .from('projects')
    .select(`
      id,
      name,
      route_lrs_key,
      spec_json,
      created_by,
      created_at,
      sessions:prototype_sessions(count)
    `)
    .eq('company_id', membership.company_id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }

  return projects || [];
};

// Get single project with sessions
export const getProject = async (projectId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get project details
  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select(`
      id,
      name,
      route_lrs_key,
      spec_json,
      created_by,
      created_at,
      company_id
    `)
    .eq('id', projectId)
    .single();

  if (projectError) throw projectError;

  const accessRole = await getProjectAccessRoleCompat(projectId, project.company_id);
  if (!accessRole) throw new Error('Access denied to this project');

  return project;
};

// Get sessions for a project
export const getProjectSessions = async (projectId) => {
  if (!projectId) {
    throw new Error('Project ID is required');
  }

  // Call the new database function
  const { data, error } = await supabase.rpc('get_project_sessions', {
    p_project_id: projectId,
  });

  if (error) {
    console.error('Error fetching project sessions with stats:', error);
    throw error;
  }

  // The RPC returns the data directly as an array
  return data || [];
};

// Create new project
export const createProject = async (projectData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get user's current company
  const { data: membership } = await supabase
    .from('memberships')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (!membership) throw new Error('No company membership found');

  const { data: project, error } = await supabase
    .from('projects')
    .insert({
      name: projectData.name,
      route_lrs_key: projectData.route_lrs_key || null,
      spec_json: projectData.spec_json || null,
      company_id: membership.company_id,
      created_by: user.id
    })
    .select()
    .single();

  if (error) throw error;

  // Create default project configuration
  const { error: configError } = await supabase
    .from('project_configurations')
    .insert({
      project_id: project.id,
      configured_by: user.id,
      // All other fields will use database defaults
    });

  if (configError) {
    console.error('Error creating project configuration:', configError);
    // Don't throw - project was created successfully, config can be added later
    // But you could throw if you want to ensure both succeed or both fail
  }

  return project;
};

export const updateProject = async (projectId, updates) => {
  const allowedFields = [
    'name',
    'route_lrs_key',
    'address',
    'city',
    'state',
    'zip_code',
    'client_name',
    'client_contact_name',
    'client_contact_email',
    'client_contact_phone',
    'project_manager',
    'description',
    'spec_json',
  ];
  const sanitizedUpdates = allowedFields.reduce((acc, field) => {
    if (updates[field] !== undefined) {
      acc[field] = updates[field];
    }
    return acc;
  }, {});

  const { data: project, error } = await supabase
    .from('projects')
    .update(sanitizedUpdates)
    .eq('id', projectId)
    .select()
    .single();

  if (error) {
    console.error('Project update error:', error);
    throw new Error(error.message);
  }

  return project;
};

// Delete project
export const deleteProject = async (projectId) => {
  // Get all sessions for this project
  const { data: sessions } = await supabase
    .from('prototype_sessions')
    .select('session_id')
    .eq('project_id', projectId);

  // Delete storage files + session_files rows BEFORE cascade
  if (sessions?.length) {
    const sessionIds = sessions.map(s => s.session_id);
    const { data: files } = await supabase
      .from('session_files')
      .select('id, storage_path')
      .in('session_id', sessionIds);

    if (files?.length) {
      const paths = files.map(f => f.storage_path).filter(Boolean);
      if (paths.length) {
        await supabase.storage.from('session-files').remove(paths);
      }
      await supabase
        .from('session_files')
        .delete()
        .in('id', files.map(f => f.id));
    }
  }

  // Also remove any upload files from jobs
  const { data: jobs } = await supabase
    .from('jobs')
    .select('storage_path')
    .eq('project_id', projectId);

  if (jobs?.length) {
    const jobPaths = jobs.map(j => j.storage_path).filter(Boolean);
    if (jobPaths.length) {
      await supabase.storage.from('session-files').remove(jobPaths);
    }
  }

  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId);

  if (error) throw error;

  return true;
};

// Add this function to your supabaseClient.js file

// Delete session and all associated data
export const deleteSession = async (sessionId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // First get the session to check ownership/access
  const { data: session, error: sessionError } = await supabase
    .from('prototype_sessions')
    .select(`
      session_id,
      project_id,
      projects!inner(company_id)
    `)
    .eq('session_id', sessionId)
    .single();

  if (sessionError) throw new Error('Session not found');

  const accessRole = await getProjectAccessRoleCompat(session.project_id, session.projects.company_id);

  if (!['owner', 'dot_admin'].includes(accessRole)) {
    throw new Error('Access denied to delete this session');
  }

  // Delete storage files via Storage API, then remove session_files rows
  // BEFORE deleting the session — prevents CASCADE from hitting storage.objects
  const { data: files } = await supabase
    .from('session_files')
    .select('id, storage_path')
    .eq('session_id', sessionId);

  if (files?.length) {
    const paths = files.map(f => f.storage_path).filter(Boolean);
    if (paths.length) {
      await supabase.storage.from('session-files').remove(paths);
    }
    await supabase
      .from('session_files')
      .delete()
      .in('id', files.map(f => f.id));
  }

  // Now delete the session (CASCADE handles segments, events, etc.)
  const { error: deleteError } = await supabase
    .from('prototype_sessions')
    .delete()
    .eq('session_id', sessionId);

  if (deleteError) throw new Error(deleteError.message);

  return { success: true, projectId: session.project_id };
};

export const uploadAndProcessProjectFile = async (file, projectId, onProgress, folderId = null, sessionName = null) => {
  if (!projectId || !file) {
    throw new Error('Project ID and file are required.');
  }

  const filePath = `session-uploads/${projectId}/${file.name}`;
  
  // Get the user's session token
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No active session. Please log in again.');
  }

  let uploadResult;

  if (file.size > 100 * 1024 * 1024) { // Over 100MB
    uploadResult = await new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'x-upsert': 'true',
        },
        metadata: {
          bucketName: 'session-files',
          objectName: filePath,
          cacheControl: '3600',
        },
        chunkSize: 6 * 1024 * 1024,
        onError: (error) => {
          console.error('TUS upload error:', error);
          reject(new Error(`Upload failed: ${error.message}`));
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percent = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          if (onProgress) {
            onProgress({ percent, bytesUploaded, bytesTotal });
          }
        },
        onSuccess: () => {
          resolve({ path: filePath });
        },
      });

      upload.start();
    });
  } else {
    // Standard upload for smaller files
    if (onProgress) {
      onProgress({ percent: 0, bytesUploaded: 0, bytesTotal: file.size });
    }

    const { data, error } = await supabase.storage
      .from('session-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      throw new Error(`Storage upload failed: ${error.message}`);
    }

    if (onProgress) {
      onProgress({ percent: 100, bytesUploaded: file.size, bytesTotal: file.size });
    }

    uploadResult = { path: data.path };
  }

  // Create job record after successful upload.  session_name carries the user-
  // typed name from the upload UI; worker uses it when creating the
  // prototype_sessions row (falls back to filename if null).
  const trimmedSessionName = typeof sessionName === 'string' ? sessionName.trim() : null;
  const { data: job, error: jobError } = await supabase
    .from('jobs')
    .insert({
      storage_path: uploadResult.path,
      project_id: projectId,
      status: 'pending',
      folder_id: folderId,
      session_name: trimmedSessionName || null,
    })
    .select()
    .single();

  if (jobError) {
    // Clean up the orphaned file since no job will process it
    await supabase.storage.from('session-files').remove([uploadResult.path]);
    throw new Error(`Job creation failed: ${jobError.message}`);
  }

  return {
    message: 'Upload complete. Processing has started on the server.',
    path: uploadResult.path,
    jobId: job.id
  };
};

// Get every pay table visible to the current user (global OR company)
export async function getPayTables() {
  const { data, error } = await supabase
    .from('pay_tables')
    .select('id,  pay_type, json_table')   // pull JSON now for legend
    .order('is_global', { ascending: false })
    

  if (error) throw error;
  return data;
}

// Patch a project with a new pay_table_id
export async function updateProjectPayTable(projectId, payTableId) {
  const { error } = await supabase
    .from('projects')
    .update({ pay_table_id: payTableId })
    .eq('id', projectId)
    .single();

  if (error) throw error;
}


export const getProjectWithConfig = async (projectId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Updated join query - now goes through project_configurations to get pay_tables
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_configurations(
          *,
          pay_tables(id, name, json_table, pay_type, description)
        )
      `)
      .eq('id', projectId)
      .single();

    if (error) {
      throw new Error(error.message);
    }

    const accessRole = await getProjectAccessRoleCompat(projectId, data.company_id);
    if (!accessRole) throw new Error('Access denied to this project');
    data.project_access_role = accessRole;
    data.is_shared_project = accessRole !== 'owner';

    // Handle the case where project_configurations might be null or empty array
    if (!data.project_configurations ||
        (Array.isArray(data.project_configurations) && data.project_configurations.length === 0)) {
      data.project_configurations = null;
    } else if (Array.isArray(data.project_configurations)) {
      // If it's an array, take the first (and should be only) configuration
      data.project_configurations = data.project_configurations[0];
    }

    return data;

  } catch (error) {
    throw error;
  }
};

export const upsertProjectConfiguration = async (projectId, config) => {
  // Sanitize numeric fields - convert empty strings to null
  const sanitizeNumeric = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  };

  // Sanitize UUID fields - convert empty strings to null
  const sanitizeUuid = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    return value;
  };

  const sanitizedConfig = {
    project_id: projectId,
    
    // Pay specification
    pay_table_id: sanitizeUuid(config.pay_table_id),
    
    // Contract parameters
    lane_width_ft: sanitizeNumeric(config.lane_width_ft),
    unit_price_per_ton: sanitizeNumeric(config.unit_price_per_ton),
    spread_rate_lbs_sy: sanitizeNumeric(config.spread_rate_lbs_sy),
    lot_length_ft: sanitizeNumeric(config.lot_length_ft),
    
    // Material properties
    material_density_lbs_cy: sanitizeNumeric(config.material_density_lbs_cy),
    pavement_type: config.pavement_type || 'A',
    
    // Profile limits
    profile_start_station: sanitizeNumeric(config.profile_start_station),
    profile_end_station: sanitizeNumeric(config.profile_end_station),
    
    // Layer quantities (for future use)
    base_2_qty: sanitizeNumeric(config.base_2_qty),
    og_layer_qty: sanitizeNumeric(config.og_layer_qty),
    base_1_qty: sanitizeNumeric(config.base_1_qty),
    intermed_qty: sanitizeNumeric(config.intermed_qty),
    surface_qty: sanitizeNumeric(config.surface_qty),
    
    // Analysis parameters
    analysis_segment_length_ft: sanitizeNumeric(config.analysis_segment_length_ft),
    iri_threshold_type_a: sanitizeNumeric(config.iri_threshold_type_a),
    iri_threshold_type_b: sanitizeNumeric(config.iri_threshold_type_b),
    
    // IRI Color Scale (JSONB field) - ADD THIS LINE
    iri_color_scale: config.iri_color_scale || null,
    
    // Contract values (for future use)
    contract_total_value: sanitizeNumeric(config.contract_total_value),
    
    // Metadata
    is_configured: true,
    configured_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('project_configurations')
    .upsert(sanitizedConfig, { 
      onConflict: 'project_id',
      ignoreDuplicates: false
    })
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

// Get project configuration only
export const getProjectConfiguration = async (projectId) => {
  const { data, error } = await supabase
    .from('project_configurations')
    .select('*')
    .eq('project_id', projectId)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
    throw new Error(error.message);
  }
  return data || null;
};

// Check if project is configured (for conditional features)
export const isProjectConfigured = async (projectId) => {
  const { data, error } = await supabase
    .from('project_configurations')
    .select('is_configured')
    .eq('project_id', projectId)
    .single();

  return data?.is_configured || false;
};

// Get projects with configuration status (for projects list) - FIXED
export const getCompanyProjectsWithConfig = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get user's current company
  const { data: membership } = await supabase
    .from('memberships')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (!membership) throw new Error('No company membership found');

  // Get projects with configuration status and session counts, filtered by company
  const { data, error } = await supabase
    .from('projects')
    .select(`
      *,
      project_configurations(is_configured),
      prototype_sessions(count)
    `)
    .eq('company_id', membership.company_id)  // <- This was missing!
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data;
};

export const getSharedProjectsWithConfig = async () => {
  const { data, error } = await supabase.rpc('get_shared_projects_with_config');
  if (error) throw new Error(error.message);

  return (data || []).map(project => ({
    ...project,
    is_shared_project: true,
    project_access_role: project.share_role,
    prototype_sessions: [{ count: Number(project.session_count || 0) }],
  }));
};

export const createProjectShareKey = async (projectId, expiresInDays = 7) => {
  const { data, error } = await supabase.functions.invoke('create-project-share-key', {
    body: { projectId, expiresInDays },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

export const redeemProjectShareKey = async (key) => {
  const { data, error } = await supabase.functions.invoke('redeem-project-share-key', {
    body: { key },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

export const getProjectShares = async (projectId) => {
  const { data, error } = await supabase
    .from('project_shares')
    .select(`
      id,
      project_id,
      owner_org_id,
      shared_org_id,
      role,
      created_at,
      updated_at,
      companies!project_shares_shared_org_id_fkey(id, name, org_type)
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
};

export const getProjectShareKeys = async (projectId) => {
  const { data, error } = await supabase
    .from('project_share_keys')
    .select('id, project_id, role, expires_at, redeemed_at, redeemed_org_id, revoked_at, created_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
};

export const revokeProjectShareKey = async (keyId) => {
  const { data, error } = await supabase
    .from('project_share_keys')
    .update({ revoked_at: new Date().toISOString() })
    .eq('id', keyId)
    .is('redeemed_at', null)
    .is('revoked_at', null)
    .select('id, revoked_at')
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const removeProjectShare = async (shareId) => {
  const { error } = await supabase
    .from('project_shares')
    .delete()
    .eq('id', shareId);

  if (error) throw new Error(error.message);
  return true;
};

// Delete project configuration
export const deleteProjectConfiguration = async (projectId) => {
  const { error } = await supabase
    .from('project_configurations')
    .delete()
    .eq('project_id', projectId);

  if (error) throw new Error(error.message);
};

// Validate configuration completeness
export const validateProjectConfiguration = (config) => {
  const requiredFields = [
    { field: 'unit_price_per_ton', label: 'Unit Price per Ton' },
    { field: 'spread_rate_lbs_sy', label: 'Spread Rate' }
  ];
  
  const missing = requiredFields.filter(({field}) => {
    const value = config[field];
    return !value || value === '' || value === null || value === undefined;
  });
  
  return {
    isValid: missing.length === 0,
    missingFields: missing.map(({label}) => label)
  };
};


// Add these helper functions to your supabaseClient.js

// Get all segments for a session (handles large datasets)
export const getSessionSegments = async (sessionId) => {
  const { data: segments, error } = await supabase
    .from('prototype_segments')
    .select('*')
    .eq('session_id', sessionId)
    .order('from_station_ft')
    .limit(50000); // High limit to get all segments

  if (error) throw error;
  return segments || [];
};

// Get all events for a session (handles large datasets)
export const getSessionEvents = async (sessionId) => {
  const { data: events, error } = await supabase
    .from('prototype_events')
    .select('*')
    .eq('session_id', sessionId)
    .order('from_station_ft')
    .limit(50000); // High limit to get all events

  if (error) throw error;
  return events || [];
};

// Get session bounds
export const getSessionBounds = async (sessionId) => {
  const { data: bounds, error } = await supabase.rpc(
    'get_prototype_session_bounds', 
    { session_id_input: sessionId }
  );
  
  if (error) throw error;
  return bounds?.length ? bounds[0] : null;
};

// Get complete session data (session + segments + events + bounds)
export const getCompleteSessionData = async (sessionId) => {
  try {
    // Fetch all data in parallel for better performance
    const [sessionData, segmentsData, eventsData, boundsData] = await Promise.all([
      supabase
        .from('prototype_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .single(),
      getSessionSegments(sessionId),
      getSessionEvents(sessionId),
      getSessionBounds(sessionId)
    ]);

    if (sessionData.error) throw sessionData.error;

    return {
      session: sessionData.data,
      segments: segmentsData,
      events: eventsData,
      bounds: boundsData
    };
  } catch (error) {
    console.error('Error fetching complete session data:', error);
    throw error;
  }
};

// Alternative: Paginated approach for extremely large datasets
export const getAllEventsPaginated = async (sessionId) => {
  let allEvents = [];
  let page = 0;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    const { data: events, error } = await supabase
      .from('prototype_events')
      .select('*')
      .eq('session_id', sessionId)
      .order('from_station_ft')
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) throw error;

    if (events.length === 0) {
      hasMore = false;
    } else {
      allEvents = allEvents.concat(events);
      page++;
      
      // Safety check to prevent infinite loops
      if (page > 100) { // Max 100k events
        console.warn('Reached maximum pagination limit for events');
        hasMore = false;
      }
    }
  }

  return allEvents;
};


// Add these functions to your supabaseClient.js file

// Get dashboard stats for current company
export const getCompanyDashboardStats = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get user's current company
    const { data: membership } = await supabase
      .from('memberships')
      .select('company_id')
      .eq('user_id', user.id)
      .single();

    if (!membership) throw new Error('No company membership found');

    // Get all sessions for company projects with distances
    const { data: sessions, error } = await supabase
      .from('prototype_sessions')
      .select(`
        session_id,
        start_station_ft,
        end_station_ft,
        created_at,
        projects!inner(company_id)
      `)
      .eq('projects.company_id', membership.company_id);

    if (error) throw error;

    // Calculate stats
    const totalSessions = sessions.length;
    
    // Calculate total miles (convert feet to miles: 1 mile = 5280 feet)
    const totalMiles = sessions.reduce((total, session) => {
      const startFt = session.start_station_ft || 0;
      const endFt = session.end_station_ft || 0;
      const distanceFt = Math.abs(endFt - startFt);
      const distanceMiles = distanceFt / 5280;
      return total + distanceMiles;
    }, 0);

    // Calculate this month's sessions
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const thisMonthSessions = sessions.filter(session => {
      const sessionDate = new Date(session.created_at);
      return sessionDate >= startOfMonth;
    }).length;

    return {
      totalSessions,
      totalMiles: Math.round(totalMiles * 10) / 10, // Round to 1 decimal place
      thisMonthSessions
    };

  } catch (error) {
    console.error('Error calculating dashboard stats:', error);
    throw error;
  }
};

// Get recent sessions across all company projects  
export const getRecentCompanySessions = async (limit = 10) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get user's current company
    const { data: membership } = await supabase
      .from('memberships')
      .select('company_id')
      .eq('user_id', user.id)
      .single();

    if (!membership) throw new Error('No company membership found');

    // Get recent sessions with project info
    const { data: sessions, error } = await supabase
      .from('prototype_sessions')
      .select(`
        session_id,
        filename,
        name,
        start_station_ft,
        end_station_ft,
        status,
        created_at,
        upload_timestamp,
        lane_no,
        direction,
        operator,
        projects!inner(
          id,
          name,
          route_lrs_key,
          company_id
        )
      `)
      .eq('projects.company_id', membership.company_id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Process sessions to add calculated fields
    const processedSessions = sessions.map(session => {
      const startFt = session.start_station_ft || 0;
      const endFt = session.end_station_ft || 0;
      const distanceFt = Math.abs(endFt - startFt);
      const distanceMiles = distanceFt / 5280;

      return {
        ...session,
        distanceMiles: Math.round(distanceMiles * 10) / 10,
        project: session.projects
      };
    });

    return processedSessions;

  } catch (error) {
    console.error('Error fetching recent sessions:', error);
    throw error;
  }
};

// Alternative: Get stats using database aggregation (more efficient for large datasets)
export const getCompanyDashboardStatsOptimized = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get user's current company
    const { data: membership } = await supabase
      .from('memberships')
      .select('company_id')
      .eq('user_id', user.id)
      .single();

    if (!membership) throw new Error('No company membership found');

    // Use a database function for aggregation (would need to be created)
    const { data: stats, error } = await supabase.rpc(
      'get_company_dashboard_stats', 
      { company_id_input: membership.company_id }
    );

    if (error) {
      // Fallback to client-side calculation if RPC doesn't exist
      console.warn('RPC function not found, falling back to client calculation');
      return await getCompanyDashboardStats();
    }

    return stats;

  } catch (error) {
    console.error('Error getting optimized dashboard stats:', error);
    // Fallback to regular calculation
    return await getCompanyDashboardStats();
  }
};

// Helper function to get relative time description
export const getRelativeTimeDescription = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Less than an hour ago';
  if (diffInHours < 2) return '1 hour ago';
  if (diffInHours < 24) return `${diffInHours} hours ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks === 1) return '1 week ago';
  if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths === 1) return '1 month ago';
  if (diffInMonths < 12) return `${diffInMonths} months ago`;
  
  return 'Over a year ago';
};

// ============================================
// SUPABASE CLIENT ADDITIONS FOR NESTED FOLDERS
// ============================================
// Add these functions to your existing supabaseClient.js

/**
 * Get folders for a project, optionally filtered by parent
 * @param {string} projectId - The project UUID
 * @param {string|null} parentFolderId - Filter by parent (null = root level)
 * @returns {Promise<Array>} Array of folder objects
 */
export async function getProjectFolders(projectId, parentFolderId = undefined) {
  let query = supabase
    .from('project_folders')
    .select('*')
    .eq('project_id', projectId)
    .order('name', { ascending: true });

  // If parentFolderId is explicitly passed (including null for root)
  if (parentFolderId !== undefined) {
    if (parentFolderId === null) {
      query = query.is('parent_folder_id', null);
    } else {
      query = query.eq('parent_folder_id', parentFolderId);
    }
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Create a new folder, optionally nested inside a parent folder
 * @param {string} projectId - The project UUID
 * @param {string} name - Folder name
 * @param {string|null} parentFolderId - Parent folder ID (null = root)
 * @returns {Promise<Object>} The created folder
 */
export async function createProjectFolder(projectId, name, parentFolderId = null) {
  const { data, error } = await supabase
    .from('project_folders')
    .insert({
      project_id: projectId,
      name: name.trim(),
      parent_folder_id: parentFolderId
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get the ancestor path for a folder (root -> parent -> current)
 * Uses the recursive SQL function
 * @param {string} folderId - The folder UUID
 * @returns {Promise<Array>} Array of ancestors, root first
 */
export async function getFolderAncestors(folderId) {
  const { data, error } = await supabase
    .rpc('get_folder_ancestors', { folder_id: folderId });

  if (error) throw error;
  return data || [];
}

/**
 * Move a session to a folder (unchanged, but included for completeness)
 * @param {string} sessionId - The session UUID
 * @param {string|null} folderId - Target folder ID (null = root)
 * @returns {Promise<Object>} The updated session
 */
export async function moveSessionToFolder(sessionId, folderId) {
  const { data, error } = await supabase
    .from('prototype_sessions')
    .update({ folder_id: folderId })
    .eq('session_id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete a folder (sessions inside are moved to root by DB trigger or handled in UI)
 * @param {string} folderId - The folder UUID
 * @returns {Promise<void>}
 */
export async function deleteProjectFolder(folderId) {
  const { error } = await supabase
    .from('project_folders')
    .delete()
    .eq('id', folderId);

  if (error) throw error;
}
